import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import Exporting from 'highcharts/modules/exporting';
import OfflineExporting from 'highcharts/modules/offline-exporting';
import HighchartsMore from 'highcharts/highcharts-more';
import Dumbbell from 'highcharts/modules/dumbbell';
import Lollipop from 'highcharts/modules/lollipop';
import Funnel from 'highcharts/modules/funnel';
import WordCloud from 'highcharts/modules/wordcloud';
import Heatmap from 'highcharts/modules/heatmap';
import Sankey from 'highcharts/modules/sankey';
import Treemap from 'highcharts/modules/treemap';
import DependencyWheel from 'highcharts/modules/dependency-wheel';
import SolidGauge from 'highcharts/modules/solid-gauge';
import Accessibility from 'highcharts/modules/accessibility';
import Pyramid3D from 'highcharts/modules/pyramid3d';
import { ZiChartOptions } from '../types/charts/chart-options';
import { useLocation } from 'react-router-dom';
import Highcharts3D from 'highcharts/highcharts-3d';
import Funnel3D from 'highcharts/modules/funnel3d';
import Cylinder from 'highcharts/modules/cylinder';
import VariablePie from 'highcharts/modules/variable-pie';
import XRange from 'highcharts/modules/xrange';
import Bullet from 'highcharts/modules/bullet';

// ZAP-2352: exporting must init before offline-exporting (the latter depends
// on it) — loaded so AI Analyst charts can call chart.exportChartLocal() for
// the "Download as PNG" toolbar button.
Exporting(Highcharts);
OfflineExporting(Highcharts);
HighchartsMore(Highcharts);
Highcharts3D(Highcharts);
Cylinder(Highcharts);
VariablePie(Highcharts);
XRange(Highcharts);
Bullet(Highcharts);
Funnel3D(Highcharts);
Pyramid3D(Highcharts);
WordCloud(Highcharts);
Funnel(Highcharts);
Dumbbell(Highcharts);
Lollipop(Highcharts);
Heatmap(Highcharts);
Sankey(Highcharts);
Treemap(Highcharts);
DependencyWheel(Highcharts);
SolidGauge(Highcharts);
Accessibility(Highcharts);

// ZAP-2352: loading the exporting module makes every Highcharts chart in the
// app sprout a context-menu "hamburger" export button by default. Disable it
// globally immediately after init — this only turns off the UI button/menu,
// not the exportChartLocal() API our custom toolbar calls directly. (Verified
// no other file sets `exporting.enabled` — TrendLineChart.tsx only disables
// `exporting.buttons.contextButton`, which deep-merges fine alongside this.)
Highcharts.setOptions({ exporting: { enabled: false } });

type RefObjectForHighchartsReact = {
	/**
	 * Chart reference
	 */
	chart: Highcharts.Chart;
	/**
	 * React reference
	 */
	container: React.RefObject<HTMLDivElement>;
};

/**
 * Must stay module-level. HighchartsReact keeps `containerProps` in its own layout-effect
 * dependency array, so an inline object literal re-fires that effect on every render and
 * repaints the chart even when none of its inputs changed.
 */
const CHART_CONTAINER_PROPS = { style: { height: '100%' } };

export const useHighChart = (
	chartRenderCallback: () => void,
	loadingChartOverride: boolean
): {
	options: ZiChartOptions;
	setOptions: (options: ZiChartOptions) => void;
	chartRender: JSX.Element;
	getChart: () => Highcharts.Chart | undefined;
} => {
	const [chartOptions, setChartOptions] = useState<ZiChartOptions>({});
	const pathname = useLocation().pathname;
	const urlEndsInNumber = /\/\d+$/.test(pathname);
	const isChartBuilder =
		(pathname.includes('analyze/charts/view/chart/') ||
			pathname.includes('analyze/charts/build/chart/')) &&
		urlEndsInNumber;

	const chartRef = useRef<RefObjectForHighchartsReact>(null);

	useEffect(() => {
		if (
			chartRef.current?.chart.chartHeight != chartOptions.height ||
			chartRef.current?.chart.chartWidth != chartOptions.width
		) {
			chartRef.current?.chart.setSize(
				(chartOptions.width as number) ?? 0,
				(chartOptions.height as number) ?? 0
			);
		}
	}, [chartOptions.height, chartOptions.width]);

	useEffect(() => {
		if (
			chartRef.current &&
			chartRef.current?.chart &&
			['wordcloud', 'heatmap', 'treemap', 'sankey'].includes(
				chartOptions.chart?.type as string
			)
		) {
			chartRef.current.chart.reflow();
		}
	}, [chartOptions.series, chartRef.current]);

	// Fonts change the space labels need, so the chart is reflowed once they are ready. Called
	// from the render body this queued a fresh callback on every render, and every one of them
	// reflowed the chart.
	useEffect(() => {
		void document.fonts.ready.then(() => {
			chartRef.current?.chart.reflow();
		});
	}, []);

	const getChart = useCallback(() => {
		return chartRef.current?.chart;
	}, []);

	const updateOptions = useCallback((options: ZiChartOptions) => {
		setChartOptions(options);
	}, []);

	const immutable = useMemo(() => {
		// wordcloud requires remount to render correctly
		const type = chartOptions.chart?.type;

		return type === 'wordcloud' || isChartBuilder;
	}, [chartOptions.chart?.type, isChartBuilder]);

	const optionsWithSeriesType = useMemo<ZiChartOptions>(() => {
		if (!chartOptions.series) return chartOptions;

		return {
			...chartOptions,
			chart: {
				...chartOptions.chart,
				style: {
					...chartOptions.chart?.style,
					fontFamily: "'Inter', 'RedHatText', 'Overpass', overpass, helvetica",
				},
			},
			series: chartOptions.series.map((s) => ({
				...s,
				type: chartOptions.chart?.type ?? s.type,
			})),
		} as ZiChartOptions;
	}, [chartOptions]);

	return {
		options: chartOptions,
		setOptions: updateOptions,
		chartRender: (
			<div
				style={{
					height: '100%',
					width: '100%',
					visibility: loadingChartOverride ? 'hidden' : 'visible',
				}}
			>
				{optionsWithSeriesType.title &&
					optionsWithSeriesType.series &&
					optionsWithSeriesType.series.length > 0 &&
					optionsWithSeriesType.series[0].type && (
						<HighchartsReact
							ref={chartRef}
							highcharts={Highcharts}
							options={optionsWithSeriesType}
							containerProps={CHART_CONTAINER_PROPS}
							immutable={immutable}
							callback={chartRenderCallback}
						/>
					)}
			</div>
		),
		getChart,
	};
};
